import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styles from './classroom-day.module.css';

// Helpers
import { getAbsenceReasonLabel } from '../../helpers/absence-helper';
import { absenceByline, contentSummary, diaryGroupByline } from '../../helpers/daily-records-helper';

type ClassroomDayProps = {
  // Dias da escola, "yyyy-MM-dd".
  day: string;
  today: string;
  classroomDay: ClassroomDiaryDay;
  fillPath: string;
  groupPath: (group: ClassroomDiaryGroup) => string;
};

const studentNames = (group: ClassroomDiaryGroup) =>
  group.entries.map((entry) => entry.student.name || 'Aluno').join(', ');

// Um card por grupo do dia: tocar nele abre a edição do grupo inteiro. Descartar fica nessa tela, não no card.
const ClassroomDay: FC<ClassroomDayProps> = ({ day, today, classroomDay, fillPath, groupPath }) => {
  const isToday = day === today;
  const { pendingWithoutDraft, drafts, published, absences } = classroomDay;

  if (pendingWithoutDraft.length === 0 && drafts.length === 0 && published.length === 0 && absences.length === 0) {
    return <p className={styles.empty}>Nenhum diário ou falta neste dia.</p>;
  }

  const pendingCardContent = (byline: string) => (
    <>
      <span className={styles.freeBadge}>Não preenchido</span>
      <p className={styles.students}>{pendingWithoutDraft.map((student) => student.name || 'Aluno').join(', ')}</p>
      <p className={styles.byline}>{byline}</p>
    </>
  );

  const groupCard = (group: ClassroomDiaryGroup) => {
    const isDraft = group.status === 'Draft';
    const summary = contentSummary(group.content);

    return (
      <article
        key={group.id}
        className={`${styles.card} ${isDraft ? styles.draftCard : ''}`}
        aria-label={isDraft ? 'Rascunho' : 'Diário enviado'}
      >
        <Link to={groupPath(group)} className={styles.cardLink}>
          <span className={isDraft ? styles.draftBadge : styles.publishedBadge}>
            {isDraft ? 'Rascunho' : 'Enviado'}
          </span>
          <p className={styles.students}>{studentNames(group)}</p>
          {summary && <p className={styles.summary}>{summary}</p>}
          {group.content.details && <p className={styles.details}>{group.content.details}</p>}
          <p className={styles.byline}>{diaryGroupByline(group)}</p>
        </Link>
        {isDraft && !isToday && <p className={styles.blocked}>Não pode mais ser enviado.</p>}
      </article>
    );
  };

  return (
    <div className={styles.grid}>
      {pendingWithoutDraft.length > 0 && (
        <article className={`${styles.card} ${styles.freeCard}`} aria-label='Não preenchido'>
          {/* Só se preenche o diário de hoje: num dia anterior o card só informa. */}
          {isToday ? (
            <Link to={fillPath} className={styles.cardLink}>
              {pendingCardContent('Preencher o diário deles')}
            </Link>
          ) : (
            <div className={styles.cardBody}>{pendingCardContent('Ficaram sem diário neste dia')}</div>
          )}
        </article>
      )}
      {drafts.map(groupCard)}
      {published.map(groupCard)}
      {absences.length > 0 && (
        <article className={`${styles.card} ${styles.absenceCard}`} aria-label='Faltas'>
          <div className={styles.cardBody}>
            <span className={styles.absentBadge}>Faltas</span>
            <ul className={styles.absences}>
              {absences.map((absence) => (
                <li key={absence.id}>
                  <span className={styles.students}>{absence.student.name || 'Aluno'}</span>
                  <span className={styles.reason}>{getAbsenceReasonLabel(absence.justification?.reason ?? null)}</span>
                  <span className={styles.byline}>{absenceByline(absence)}</span>
                </li>
              ))}
            </ul>
          </div>
        </article>
      )}
    </div>
  );
};

export default ClassroomDay;
